import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Send } from 'lucide-react';
import { getErrorMessage, getErrorStatus, lessonService } from '../services/api';
import type { LessonIssueCategory, LessonIssueReport } from '../types/api';
import { Modal, Spinner } from './ui';
import { useToast } from './Toast';

// What the lesson page knows when the dialog sends (read then, not when it opened: a run may end meanwhile).
export type IssueReportContext = Omit<LessonIssueReport, 'category' | 'description' | 'code'> & { code: string };

const CATEGORIES: Array<{ id: LessonIssueCategory; label: string; hint: string }> = [
  { id: 'theory', label: 'Theory is unclear or wrong', hint: 'A mistake, a gap or a confusing explanation' },
  { id: 'code', label: 'Starter code or exercise problem', hint: "The starter code fails, or the task can't be done" },
  { id: 'simulation', label: "Simulation doesn't work", hint: "It doesn't load, crashes or behaves oddly" },
  { id: 'other', label: 'Something else', hint: '' },
];
// The backend's limits (app/schemas/issue_report.py).
const MIN_DESCRIPTION = 10;
const MAX_DESCRIPTION = 5000;

/**
 * "Report an issue" on the lesson page (WRK-21): the report becomes an issue for the lessons' maintainers. The
 * student's code goes along only when they tick the box; the page's context (last run, simulation status, browser)
 * always does, and the dialog says so.
 */
const ReportIssueDialog: React.FC<{
  lessonId: number;
  lessonTitle: string;
  getContext: () => IssueReportContext;
  onClose: () => void;
}> = ({ lessonId, lessonTitle, getContext, onClose }) => {
  const toast = useToast();
  const [category, setCategory] = useState<LessonIssueCategory | null>(null);
  const [description, setDescription] = useState('');
  const [includeCode, setIncludeCode] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const tooShort = description.trim().length < MIN_DESCRIPTION;
  // On a phone the dialog scrolls: bring a failure's message into view.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [error]);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!category || tooShort || sending) return;
    setSending(true);
    setError('');
    const { code, ...context } = getContext();
    try {
      await lessonService.reportIssue(lessonId, { ...context, category, description: description.trim(), code: includeCode ? code : null });
      toast.success('Thanks, your report was sent.');
      onClose();
    } catch (sendError) {
      const status = getErrorStatus(sendError);
      // The dialog stays open with the text, so nothing typed is lost; 503 and 429 carry the backend's own message.
      setError(status === 503 || status === 429
        ? getErrorMessage(sendError)
        : getErrorMessage(sendError, "Your report couldn't be sent. Please try again in a few minutes."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Report an issue"
      description={<>About <span className="font-medium text-gray-700 dark:text-gray-200">{lessonTitle}</span>. Reports go to the lesson's authors.</>}
      onClose={onClose}
    >
      <form onSubmit={send} className="space-y-5">
        <fieldset>
          <legend className="label">What's wrong?</legend>
          <div className="space-y-2">
            {CATEGORIES.map((option, index) => (
              <label
                key={option.id}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${category === option.id
                  ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                  : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60'}`}
              >
                <input
                  type="radio"
                  name="issue-category"
                  value={option.id}
                  checked={category === option.id}
                  onChange={() => setCategory(option.id)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600"
                  autoFocus={index === 0}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">{option.label}</span>
                  {option.hint && <span className="block text-xs text-gray-500 dark:text-gray-400">{option.hint}</span>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="label" htmlFor="issue-description">Describe the issue</label>
          <textarea
            id="issue-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="input resize-y"
            rows={4}
            maxLength={MAX_DESCRIPTION}
            required
            aria-describedby="issue-description-hint"
            placeholder="What did you expect, and what happened instead?"
          />
          <p id="issue-description-hint" className="hint flex justify-between gap-3">
            <span>{tooShort ? `At least ${MIN_DESCRIPTION} characters.` : ''}</span>
            <span className="tabular-nums shrink-0">{description.length} / {MAX_DESCRIPTION}</span>
          </p>
        </div>

        <div>
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={includeCode}
              onChange={(event) => setIncludeCode(event.target.checked)}
              className="checkbox mt-0.5"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Include my current code</span>
          </label>
          <p className="hint">
            The report also includes the last run's results, the simulation's status and your browser and screen size,
            but not your name or email.
          </p>
        </div>

        {error && (
          <div ref={errorRef} className="alert alert-error" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" disabled={!category || tooShort || sending} className="btn btn-primary">
            {sending ? <Spinner className="w-4 h-4 text-white" /> : <Send />}
            {sending ? 'Sending…' : 'Send report'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ReportIssueDialog;
